import { KeyboardEvent, useRef, useState } from 'react';
import { Keyboard, Package } from 'lucide-react';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { useMoveStore } from 'store/moveStore';
import { scanPallet } from 'services/api/palletBuild';
import { playErrorSound, playGoodSound } from 'utils/sounds';

export const MoveScanPage = () => {
  const [scanValue, setScanValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { setCurrentScreen } = useAppStore();
  const { setPallet, reset } = useMoveStore();

  const handleSubmit = async () => {
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await scanPallet(trimmed);
      if (data.success) {
        setPallet(data.data[0]);
        playGoodSound();
        setScanValue('');
        setCurrentScreen(SCREEN_TYPES.MOVE_DESTINATION);
      } else {
        setErrorMessage('Failed to find pallet. Please try again.');
        setHasError(true);
        playErrorSound();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const onCancel = () => {
    reset();
    setScanValue('');
    setHasError(false);
    setCurrentScreen(SCREEN_TYPES.CALLBACK);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-4',
        hasError && `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 ${DISPOSITION_COLORS.PROBLEM.gradient}`
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-widest',
            hasError ? 'text-white/80' : 'text-blue-500'
          )}
        >
          Move Pallet
        </span>
        <h2 className={cn('text-2xl font-bold', hasError ? 'text-white' : 'text-slate-800')}>
          Scan Pallet
        </h2>
        {hasError && <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>}
      </div>

      <div className="flex items-center gap-3 rounded-xl bg-blue-50 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
          <Package className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
            Pallet
          </span>
          <span className="text-sm font-medium text-slate-500 italic">Not scanned yet</span>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">Input Field</Label>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Scan pallet ID..."
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="text-base"
            disabled={isLoading}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container">
        <Button onClick={onCancel} variant="secondary" className="flex-1 h-12">
          Cancel
        </Button>
      </div>
    </div>
  );
};
